import { OrdersModel } from '@pukka/api';
import { Text } from '@pukka/ui-components';
import { format, isValid } from 'date-fns';

import { invoiceReceiptDateTime } from './utils';

interface ReceiptInfoProps {
  readonly invoiceDetail: OrdersModel.OrderDetail;
  readonly invoiceRefund?: OrdersModel.OrderRefund;
}

interface ReceiptInfoDetailProps {
  readonly label: string;
  readonly value?: string | number;
  readonly values?: (string | null)[];
}

function ReceiptInfoDetail({ label, value, values }: ReceiptInfoDetailProps) {
  return (
    <div className="mb-1">
      {label && (
        <Text variant="body-1" weight="semibold">
          {label}
        </Text>
      )}
      {values?.filter(Boolean).map((val, idx) => (
        <Text variant="body-1" key={idx}>
          {val}
        </Text>
      ))}
      {!values && <Text variant="body-1">{value}</Text>}
    </div>
  );
}

export default function ReceiptInfo({ invoiceDetail, invoiceRefund }: ReceiptInfoProps) {
  const dueDate = new Date(invoiceDetail.invoice_due_date);

  return (
    <div className="w-full rounded-lg bg-neutral-50 px-4 py-3">
      <div className="mb-4 flex w-full flex-wrap">
        <div className="flex-1">
          <ReceiptInfoDetail label="Served By" value={invoiceDetail.server_name || '-'} />
          <ReceiptInfoDetail label="Collected By" value={invoiceDetail.collector_name} />
          {invoiceDetail.table_name && <ReceiptInfoDetail label="Table Name" value={invoiceDetail.table_name} />}
          <ReceiptInfoDetail label="Customer" values={[invoiceDetail.customer_name, invoiceDetail.customer_email]} />
        </div>

        <div className="flex-1 text-right">
          <ReceiptInfoDetail label="Invoice Number" value={invoiceDetail.invoice_no} />
          <ReceiptInfoDetail label="Invoice Date" value={invoiceReceiptDateTime(invoiceDetail.created_at, 'datetime')} />
          {invoiceRefund?.refunded_by && <ReceiptInfoDetail label="Cancelled By" value={invoiceRefund.refunded_by} />}
          {invoiceRefund?.refunded_reason && <ReceiptInfoDetail label="Reason for Cancellation" value={invoiceRefund.refunded_reason} />}
        </div>
      </div>

      {isValid(dueDate) && (
        <Text
          variant="body-1"
          weight="semibold"
          className={`mb-4 border-2 p-2 text-center ${
            invoiceDetail.invoice_status !== 'paid' ? 'border-red-500 text-red-500' : 'border-black'
          }`}
        >
          Payment Due by {format(dueDate, 'dd MMM yyyy')}
        </Text>
      )}
    </div>
  );
}
